import React from "react";

export const currencyFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 2, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  maximumFractionDigits: 2, // (causes 2500.99 to be printed as $2,501)
});

export const shortDateFormatter = (d: string) => new Date(d).toDateString();

// Custom React hook to use localStorage to store state rather than the page context
export const useStickyState = <T>(defaultValue: T, key: string): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [value, setValue] = React.useState<T>(() => {
    const stickyValue = window.localStorage.getItem(key);

    if (stickyValue === null || stickyValue === undefined) return defaultValue;

    try {
      return JSON.parse(stickyValue);
    }
    catch (e) {
      // Not an object - must be a raw value
      return stickyValue as T;
    }
  });

  // Create a wrapped setValue that ensures localStorage is updated
  const wrappedSetValue = React.useCallback((newValue: React.SetStateAction<T>) => {
    setValue(prevValue => {
      const actualNewValue = typeof newValue === 'function' ? (newValue as (prevState: T) => T)(prevValue) : newValue;
      
      // Update localStorage immediately. This runs inside a React state
      // update, so a failure here (typically QuotaExceededError when the
      // value is large) would otherwise crash the render and blank the page.
      // Persistence is a convenience: on failure keep the in-memory value and
      // drop the stale stored copy.
      try {
        if (actualNewValue !== null && actualNewValue !== undefined) {
          window.localStorage.setItem(key, JSON.stringify(actualNewValue));
        } else {
          window.localStorage.removeItem(key);
        }
      } catch (e) {
        console.warn(`Could not persist "${key}" to localStorage:`, e);
        try { window.localStorage.removeItem(key); } catch { /* ignore */ }
      }
      
      return actualNewValue;
    });
  }, [key]);

  // Return the current value and the wrapped setter
  return [value, wrappedSetValue];
};

